import { Avatar, AvatarProps } from "antd";
import { User } from "lucide-react";
import { useState } from "react";

interface SafeAvatarProps extends AvatarProps {
    src?: string;
}

/**
 * SafeAvatar component that validates URLs and handles broken image errors
 * to prevent unnecessary network requests to invalid URLs
 */
const SafeAvatar = ({ src, ...props }: SafeAvatarProps) => {
    const [hasError, setHasError] = useState(false);
    const [isValidUrl, setIsValidUrl] = useState(true);

    // Validate URL before using it
    const validateUrl = (url?: string): boolean => {
        if (!url || typeof url !== 'string') {
            return false;
        }

        // Trim whitespace
        const trimmedUrl = url.trim();
        if (!trimmedUrl) {
            return false;
        }

        // Reject very short URLs that are likely typos (like "6" or "/6")
        // Increase threshold to catch more edge cases
        if (trimmedUrl.length <= 3) {
            return false;
        }

        // First, check the raw URL string for suspicious patterns before parsing
        // This catches cases like "m.stripe.com/6" that would be treated as relative paths
        if (trimmedUrl.includes('stripe.com') && !trimmedUrl.includes('microtica')) {
            return false;
        }

        // Reject URLs that look like they might be typos (single digit or very short paths)
        // Match patterns like "6", "/6", "m.stripe.com/6", etc.
        if (/^\/?\d+$/.test(trimmedUrl) || /^\/?[a-z0-9]+\.[a-z]+\/\d+$/i.test(trimmedUrl)) {
            return false;
        }

        // Check if it's a valid URL format
        try {
            // Try parsing as absolute URL first
            const urlObj = new URL(trimmedUrl);
            
            // Must be http or https
            if (!urlObj.protocol.startsWith('http')) {
                return false;
            }
            
            // Reject suspicious domains like stripe.com when not expected (double-check after parsing)
            if (urlObj.hostname.includes('stripe.com') && !urlObj.hostname.includes('microtica')) {
                return false;
            }
            
            // Reject very short paths that might be typos (like "/6")
            if (urlObj.pathname.length <= 2 && urlObj.pathname !== '/') {
                return false;
            }
            
            return true;
        } catch {
            // If URL parsing fails, it might be a relative path
            // Only allow relative paths that start with / and are meaningful
            if (trimmedUrl.startsWith('/')) {
                // Allow relative paths but validate they're not too short and look valid
                // Reject paths that are just "/" followed by a single digit or very short
                if (trimmedUrl.length <= 3 || /^\/\d+$/.test(trimmedUrl)) {
                    return false;
                }
                // For relative paths, we'll allow them but they should be longer
                return trimmedUrl.length > 3;
            }
            
            // Reject anything that doesn't start with http/https or /
            // This catches cases like "m.stripe.com/6" which would be treated as relative
            // and cause unwanted requests
            return false;
        }
    };

    const safeSrc = isValidUrl && validateUrl(src) ? src : undefined;

    // If URL is invalid or has error, show icon instead
    if (!safeSrc || hasError) {
        return <Avatar {...props} icon={<User size={16} />} />;
    }

    return (
        <Avatar
            {...props}
            src={safeSrc}
            onError={() => {
                setHasError(true);
                return true;
            }}
        />
    );
};

export default SafeAvatar;

